import {IActionDispatcher, IPaylodAction} from '../../interfaces/dispatchers'
import {
  FieldDescription,
  INamespacedXyDbFieldInfos,
  ISourceDescriptions,
  IXyDbFieldInfos
} from '../../interfaces/models'

import {DeniedServiceInfo, FailedServiceInfo, Projection} from '../../models'

import axios from 'axios'

import {IFeatureLayerInfo, IPropertyTypes} from '../../interfaces/models'
import {IStore} from '../../interfaces/reducers'

import {Extent} from 'ol/extent'
import {LayerInfosService} from '..'
import LayerInfosActions from '../../enums/ActionTypes/LayerInfosActions'
import {GeometryType} from '../../enums/GeometryType'
import {ICrudService} from '../../interfaces/services'
import {Feature, ImageLayer, TileLayer} from '../../models'

export class XyGisLayerInfosService extends LayerInfosService {
  crudService: ICrudService
  constructor(XyGisCrudService: ICrudService) {
    super()
    this.crudService = XyGisCrudService
  }
  public propToCommonType(type: string): IPropertyTypes | null {
    switch (type) {
      case 'String':
        return 'string'
      case 'Enum':
        return 'enum'
      case 'Int32':
      case 'Int16':
      case 'Int64':
      case 'Byte':
        return 'integer'
      case 'xsd:date-time':
        return 'date'
      case 'DateTime':
      case 'DateTimeOffset':
        return 'date'
      case 'Double':
      case 'Single':
        return 'double'
      case 'xsd:string':
        return 'string'
      case 'xsd:string':
        return 'string'
      default:
        return null
    }
  }

  public getVectorLoadFunction(
    layerInfo: IFeatureLayerInfo,
    getStore: () => IStore,
    dispatch: (data: IPaylodAction | IActionDispatcher) => void
  ): (extent: Extent, resolution: number, projection: Projection) => Promise<Array<Feature>> {
    return (extent: Extent, resolution: number, projection: Projection) => {
      const setLoading = (loading: boolean) => {
        const loadingLayers = {...getStore().layerInfos.loadingLayers}
        if (loading) {
          loadingLayers[layerInfo.url] = true
        } else {
          delete loadingLayers[layerInfo.url]
        }
        dispatch({
          type: LayerInfosActions.SET_LOADING_LAYERS,
          payload: loadingLayers
        })
      }
      let where = layerInfo.sourceDescription.constantSqlWhere
      const filters = getStore().dataQueries[layerInfo.sourceDescription.id]
      if (filters && filters.mapDataWhere) {
        where = where + ' AND ' + filters.mapDataWhere
      }

      return new Promise((resolve, reject) => {
        setLoading(true)
        this.crudService
          .execute(layerInfo, {
            where: where,
            outFields: ['*'],
            returnGeometry: true,
            geometry: getStore().map.mapService.getExtent().getPolygon()
          })
          .then(features => {
            setLoading(false)
            resolve(features)
          })
          .catch(error => {
            console.error(error)
            resolve([])
            setLoading(false)
          })
      })
    }
  }

  public createImageLayer(
    layerInfos: IFeatureLayerInfo[],
    minRes: number,
    maxRes: number,
    type: 'tile' | 'image' | 'xyz',
    getStore: () => IStore
  ): TileLayer | ImageLayer {
    throw new Error('use wfs manager instead')
  }
  protected isGeometryColumn(column: any, featureUrl: string): boolean {
    return (
      column.Type == 'Point' ||
      column.Type == 'Polygon' ||
      column.Type == 'LineString' ||
      column.Type == 'MultiPolygon' ||
      column.Type == 'MultiLineString'
    )
  }

  protected getGeometryField(data: any[], featureUrl: string): FieldDescription | null {
    for (let i = 0; i < data.length; i++) {
      const column = data[i]
      if (this.isGeometryColumn(column, featureUrl)) {
        if (
          column.Type == 'Point' ||
          column.Type == 'Polygon' ||
          column.Type == 'LineString' ||
          column.Type == 'MultiPolygon' ||
          column.Type == 'MultiLineString'
        ) {
          return {
            name: column.ColumnName,
            type: 'geometry',
            nullable: true,
            length: 255,
            geometryType: column.Type
          }
        }
        return {
          // in case these are point fields like X , Y or lat , lng
          name: 'geometry',
          type: 'geometry',
          nullable: true,
          length: 255,
          geometryType: GeometryType.Point
        }
      }
    }
    return null
  }

  protected glueDescriptions(
    sourceDscriptions: ISourceDescriptions,
    descriptions: IXyDbFieldInfos,
    baseUrl: string,
    namespace: string
  ) {
    const layerToFieldDescriptions = {} as {[layerUrl: string]: Array<FieldDescription>}
    for (const featureName in descriptions) {
      const featureUrl = `${baseUrl}/${namespace}/${featureName}`
      layerToFieldDescriptions[featureUrl] = []
      const geometryField = this.getGeometryField(descriptions[featureName], featureUrl)
      descriptions[featureName].forEach(p => {
        const type = this.propToCommonType(p.Type)
        if (type) {
          layerToFieldDescriptions[featureUrl].push({
            name: p.ColumnName,
            type: type,
            nullable: p.Nullable,
            length: p.length,
            geometryType: GeometryType.None
          })
        }
      })
      if (geometryField) {
        layerToFieldDescriptions[featureUrl].push(geometryField)
      }
    }
    return layerToFieldDescriptions
  }

  protected fetchFeatureDescription(
    sourceDscriptions: ISourceDescriptions
  ): Promise<Array<IFeatureLayerInfo | FailedServiceInfo | DeniedServiceInfo>> {
    const firstKey = Object.keys(sourceDscriptions)[0]
    const baseUrl = sourceDscriptions[firstKey].baseUrl
    const namespace = sourceDscriptions[firstKey].nameSpace

    return new Promise((resolve, reject) => {
      axios
        .get(`${baseUrl}/Database/DescribeFeatures?nameSpace=${namespace}`)
        .then(response => {
          const descriptions = response.data as IXyDbFieldInfos
          const layerToFieldDescriptions = this.glueDescriptions(sourceDscriptions, descriptions, baseUrl, namespace)
          resolve(this.createFeatureLayerInfo(layerToFieldDescriptions, sourceDscriptions))
        })
        .catch((error: Error) => {
          //TODO - remove hard coded 401
          resolve(this.createFailedLayerInfo(error, 401, sourceDscriptions))
        })
    })
  }

  protected getFeatureDescription(
    sourceDscriptions: ISourceDescriptions,
    descriptions: IXyDbFieldInfos
  ): Array<IFeatureLayerInfo | FailedServiceInfo | DeniedServiceInfo> {
    const firstKey = Object.keys(sourceDscriptions)[0]
    const baseUrl = sourceDscriptions[firstKey].baseUrl
    const namespace = sourceDscriptions[firstKey].nameSpace

    const layerToFieldDescriptions = this.glueDescriptions(sourceDscriptions, descriptions, baseUrl, namespace)
    return this.createFeatureLayerInfo(layerToFieldDescriptions, sourceDscriptions)
  }

  public fetchFeatureDescriptions(namespaceGroupedSourceDescriptions: {
    [namespaceUrl: string]: ISourceDescriptions
  }): Promise<Array<IFeatureLayerInfo | FailedServiceInfo | DeniedServiceInfo>> {
    const namespaceInfoPromises: Array<Promise<Array<IFeatureLayerInfo | FailedServiceInfo | DeniedServiceInfo>>> = []
    for (const namespacedUrl in namespaceGroupedSourceDescriptions) {
      if (Object.keys(namespaceGroupedSourceDescriptions[namespacedUrl]).length > 0) {
        namespaceInfoPromises.push(this.fetchFeatureDescription(namespaceGroupedSourceDescriptions[namespacedUrl]))
      }
    }
    return this.resolveNamespacePromises(namespaceInfoPromises)
  }

  public getFeatureDescriptions(
    namespaceGroupedSourceDescriptions: {[namespaceUrl: string]: ISourceDescriptions},
    namespacedXyDbFieldInfos: INamespacedXyDbFieldInfos
  ): Array<IFeatureLayerInfo | FailedServiceInfo | DeniedServiceInfo> {
    let infos: Array<IFeatureLayerInfo | FailedServiceInfo | DeniedServiceInfo> = []
    for (const namespacedUrl in namespaceGroupedSourceDescriptions) {
      console.warn('here')
      if (
        Object.keys(namespaceGroupedSourceDescriptions[namespacedUrl]).length > 0 &&
        namespacedXyDbFieldInfos[namespacedUrl]
      ) {
        infos = infos.concat(
          this.getFeatureDescription(
            namespaceGroupedSourceDescriptions[namespacedUrl],
            namespacedXyDbFieldInfos[namespacedUrl]
          )
        )
      }
    }
    return infos
  }
}
